import { RADAR_PLAY_FRAME_MS, RADAR_PLAY_HOLD_MS, RADAR_REFRESH_MS } from "@divespots/contracts";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { api, isApiRequestError, useRadarIndex } from "../../api/index.ts";
import { RadarFrameStore } from "./frames.ts";
import type { FrameState } from "./frames.ts";
import { buildSteps, selectStep, usableFrames } from "./steps.ts";
import type { BarStep, ForecastAxis } from "./steps.ts";
import { useNow, usePageVisible } from "./useNow.ts";

export interface RadarBar {
  /** Radar steps exist and are fresh: the bar is the one-bar layout. */
  active: boolean;
  /** The radar failed or is stale while "Niederschlag" is on: the bar is the forecast bar plus a notice. */
  unavailable: boolean;
  steps: BarStep[];
  /** Selected step. */
  index: number;
  /** Newest frame ("Jetzt · gemessen"), first forecast step; -1 when absent. */
  jetzt: number;
  divider: number;
  t: number | null;
  kind: BarStep["kind"] | null;
  /** Picture state of the selected radar step; undefined at forecast steps. */
  frame: FrameState | undefined;
  setIndex: (i: number) => void;
  playing: boolean;
  togglePlay: () => void;
  now: number;
}

interface Opts {
  enabled: boolean;
  forecast: ForecastAxis | null;
}

/**
 * Radar part of the one time bar: the frame index (polled), the frames (newest first, then in the
 * background), the joined radar + forecast steps, the selection and the play loop.
 */
export function useRadar({ enabled, forecast }: Opts): RadarBar {
  const visible = usePageVisible();
  const now = useNow();
  const index = useRadarIndex(enabled, visible);
  // a tab that was hidden may have missed refreshes: ask again when it comes back and the data is old
  const { refetch, dataUpdatedAt } = index;
  useEffect(() => {
    if (enabled && visible && Date.now() - dataUpdatedAt >= RADAR_REFRESH_MS && dataUpdatedAt > 0)
      void refetch();
  }, [enabled, visible, dataUpdatedAt, refetch]);

  const frames = useMemo(
    () => (enabled ? usableFrames(index.data?.frames, now) : []),
    [enabled, index.data, now],
  );
  // the key keeps the arrays stable across clock ticks that change nothing
  const framesKey = frames.join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableFrames = useMemo(() => frames, [framesKey]);
  const axisKey = forecast ? `${forecast.min}:${forecast.max}:${forecast.step}` : "";
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const axis = useMemo(() => forecast, [axisKey]);
  const built = useMemo(() => buildSteps(stableFrames, axis), [stableFrames, axis]);
  const active = stableFrames.length > 0;

  const [picked, setPicked] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const selected = active ? selectStep(built.steps, picked, built.jetzt) : -1;
  const step = selected >= 0 ? built.steps[selected]! : null;
  const radarTimes = stableFrames;

  const [store] = useState(() => new RadarFrameStore((t, signal) => api.radarFrame(t, signal)));
  useEffect(() => () => store.dispose(), [store]);
  useSyncExternalStore(store.subscribe, store.getVersion);
  const frameTime = step?.kind === "radar" ? step.t : null;
  // while playing, the next frame of the loop is wanted as well: the loop never waits for the background
  const nextTime =
    playing && selected >= 0
      ? (radarTimes[selected >= radarTimes.length - 1 ? 0 : selected + 1] ?? null)
      : null;
  useEffect(() => {
    if (!enabled) store.sync([], null);
    else
      store.sync(
        radarTimes,
        nextTime === null || frameTime === null ? frameTime : [frameTime, nextTime],
      );
  }, [store, enabled, radarTimes, frameTime, nextTime]);

  // play loop over the radar steps: one step per RADAR_PLAY_FRAME_MS, RADAR_PLAY_HOLD_MS on the newest
  const lastRadar = stableFrames.length - 1;
  const stepRef = useRef({ selected, lastRadar, radarTimes });
  stepRef.current = { selected, lastRadar, radarTimes };
  const waiting = useRef<number | null>(null);
  const advanceTo = useCallback(
    (i: number) => setPicked(stepRef.current.radarTimes[i] ?? null),
    [],
  );

  useEffect(() => {
    if (!playing || !active || !visible) return;
    const at = selected;
    if (at < 0 || at > lastRadar) return;
    const next = at >= lastRadar ? 0 : at + 1;
    const timer = setTimeout(
      () => {
        const t = stepRef.current.radarTimes[next];
        if (t !== undefined && !store.settled(t)) waiting.current = next;
        else advanceTo(next);
      },
      at >= lastRadar ? RADAR_PLAY_HOLD_MS : RADAR_PLAY_FRAME_MS,
    );
    return () => clearTimeout(timer);
  }, [playing, active, visible, selected, lastRadar, store, advanceTo]);
  // a frame the loop waited for has arrived (or failed)
  const version = store.getVersion();
  useEffect(() => {
    const w = waiting.current;
    if (w === null) return;
    const t = stepRef.current.radarTimes[w];
    if (t === undefined || store.settled(t)) {
      waiting.current = null;
      advanceTo(w);
    }
  }, [version, store, advanceTo]);
  useEffect(() => {
    if (!active || !visible) setPlaying(false);
  }, [active, visible]);
  useEffect(() => {
    if (!enabled) setPlaying(false);
  }, [enabled]);

  const builtRef = useRef(built);
  builtRef.current = built;
  const setIndex = useCallback((i: number) => {
    setPlaying(false);
    waiting.current = null;
    setPicked(builtRef.current.steps[i]?.t ?? null);
  }, []);
  const playingRef = useRef(playing);
  playingRef.current = playing;
  const togglePlay = useCallback(() => {
    if (playingRef.current) {
      setPlaying(false);
      return;
    }
    // start from the oldest frame when the bar is on a forecast step or on the newest frame
    const s = stepRef.current;
    if (s.selected < 0 || s.selected >= s.lastRadar) setPicked(s.radarTimes[0] ?? null);
    setPlaying(true);
  }, []);

  // a 404 is an API without the radar (weather off as a whole): the plain forecast bar, no notice
  const gone = isApiRequestError(index.error) && index.error.status === 404;
  const failed = enabled && !gone && (index.isError || (index.data !== undefined && !active));
  return {
    active,
    unavailable: failed && !active,
    steps: built.steps,
    index: selected,
    jetzt: active ? built.jetzt : -1,
    divider: built.divider,
    t: step?.t ?? null,
    kind: step?.kind ?? null,
    frame: frameTime === null ? undefined : store.get(frameTime),
    setIndex,
    playing,
    togglePlay,
    now,
  };
}
